<script lang="ts">
  import { api } from '../lib/api'
  import { accountTypeLabel, formatDate, formatMoney, isPremium } from '../lib/format'
  import type { Account, Money } from '../lib/types'
  import Notice from '../lib/components/Notice.svelte'
  import Loading from '../lib/components/Loading.svelte'

  /*
   * Account summary — the Website's /account, field for field.
   *
   * TWO CONTEXTS ON ONE PAGE. Everything here is account state except the premium credit, which is
   * Stripe's balance and therefore the billing API's. That is the two-context split doing its job rather
   * than a wrinkle to smooth over — see design `platform-architecture` §6a.2b.
   *
   * The credit call is DELIBERATELY NOT FATAL. Billing lives in BirdJournal.Integrations.Stripe, which
   * does not exist yet, and a summary that refuses to render because an unrelated context is down would
   * be a worse page than one missing an optional row.
   */

  let account = $state<Account | null>(null)
  let credit = $state<Money | null>(null)
  let failed = $state(false)

  async function load() {
    try {
      account = await api.account()
    } catch {
      failed = true
      return
    }
    try {
      credit = (await api.subscription()).credit
    } catch {
      credit = null
    }
  }

  load()
</script>

{#if failed}
  <Notice tone="error">We could not load your account just now. Please try again shortly.</Notice>
{:else if !account}
  <Loading rows={5} />
{:else}
  <div class="rows">
    <div class="row">
      <div class="row-label">Email</div>
      <div class="row-value">{account.email}</div>
    </div>

    <div class="row">
      <div class="row-label">Account created on</div>
      <div class="row-value">{formatDate(account.createdAt)}</div>
    </div>

    <div class="row">
      <div class="row-label">Account type</div>
      <div class="row-value">{accountTypeLabel(account)}</div>
    </div>

    {#if isPremium(account)}
      <div class="row">
        <div class="row-label">Premium auto-renew</div>
        <div class="row-value">{account.premium.autoRenew ? 'Enabled' : 'Disabled'}</div>
      </div>

      <!-- The same date, named for what it means. "Expires" and "renews" are the difference between a
           warning and a reassurance, and the Website already made that distinction. -->
      {#if account.premium.until}
        <div class="row">
          <div class="row-label">
            {account.premium.autoRenew ? 'Premium renews on' : 'Premium expires on'}
          </div>
          <div class="row-value">{formatDate(account.premium.until)}</div>
        </div>
      {/if}
    {/if}

    {#if credit}
      <div class="row">
        <div class="row-label">Premium credit</div>
        <div class="row-value">{formatMoney(credit)}</div>
      </div>
    {/if}

    <div class="row">
      <div class="row-label">Premium months earned</div>
      <div class="row-value">
        {account.referral.monthsEarned} of {account.referral.monthsCap}
        <span class="aside">
          — <a href="/share">share Bird Journal</a> to earn free months of Bird Journal Premium
        </span>
      </div>
    </div>
  </div>

  <p class="contact">
    To make changes to your account, or if you have any queries, please email
    <a href="mailto:mail@birdjournal.com">mail@birdjournal.com</a>.
  </p>
{/if}

<style>
  .aside {
    font-weight: 400;
    color: var(--bj-on-surface-variant);
  }
  .contact {
    margin-top: var(--space-6);
    color: var(--bj-on-surface-variant);
  }
</style>
